import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { stackActions } from 'actions/stackActions';
import { makeStyles } from '@material-ui/core/styles';
import {
  Divider,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
} from '@material-ui/core';
import { PageToolbar } from 'components/PageToolbar';
import {
  DateRangePickerComponent,
  now,
  lastMonth,
} from 'components/DateRangePicker';
import { ApplicationState } from 'store';
import { StacksMap } from 'features/stacks/StacksMap';

import OverlaySpinner from 'components/spinners/OverlaySpinner';
import MultiSelectFilter from 'components/MultiSelectFilter';
import { userActions } from 'actions/userActions';
import { SawmillStacksTable } from 'features/stacks/SawmillStacksTable';
import { StacksBySawmillQueryPagedRequest } from '.generated/api';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '100%',
  },
  toolbar: {
    display: 'flex',
  },
  filterContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterRow: {
    marginTop: theme.spacing(1),
  },
  overlaySpinner: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  datePickerContainer: {
    padding: theme.spacing(2),
  },
  flexGrow: {
    flexGrow: 1,
  },
  map: {
    width: '100%',
    height: '33vh',
    minHeight: '33vh',
  },
  table: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  filterSelect: {
    width: '18rem',
  },
  filterButtonRoot: {
    margin: 0,
    minWidth: 0,
    padding: 14,
  },
  filtersApplied: {
    marginLeft: theme.spacing(2),
  },
  selectContainer: {
    maxWidth: '100%', // ensures ellipsis in select dropdown
  },
}));

const SawmillStacks: React.FC = () => {
  const defaultPageSize = 50;
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState<
    StacksBySawmillQueryPagedRequest | undefined
  >(undefined);

  const [organizationOptions, setOrganizationOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<
    string[]
  >([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    [],
  );

  const [showMap, setShowMap] = useState<boolean>(true);

  const { stacks, isLoadingStacks, isLoadingCoordinates, updatedAt } =
    useSelector((state: ApplicationState) => state.stacks);

  const sawmillUserInfo = useSelector(
    (state: ApplicationState) => state.isLoggedIn.sawmillUserInfo,
  );

  const updateListFilter = (name: string, values: string[]) => {
    const query = new URLSearchParams(location.search);
    query.delete(name);
    for (const value of values) {
      query.append(name, value);
    }
    history.replace({ search: query.toString() });
  };

  const handleDateRangeChange = (
    from: Date | null,
    to: Date | null,
    valid: boolean,
  ) => {
    if (!valid) {
      return;
    }

    const query = new URLSearchParams(location.search);
    if (from) {
      query.set('from', from.toISOString());
    } else {
      query.delete('from');
    }

    if (to) {
      query.set('to', to.toISOString());
    } else {
      query.delete('to');
    }

    history.replace({ search: query.toString() });
  };

  const handlePageChanged = (newPage: number) => {
    const query = new URLSearchParams(location.search);
    if (newPage === 0) {
      query.delete('page'); // default page 1
    } else {
      query.set('page', (newPage + 1).toString());
    }
    history.push({ search: query.toString() });
  };

  const handlePageSizeChanged = (pageSize: number) => {
    const query = new URLSearchParams(location.search);
    query.delete('page'); // default page 1
    if (pageSize === defaultPageSize) {
      query.delete('pageSize');
    } else {
      query.set('pageSize', pageSize.toString());
    }
    history.push({ search: query.toString() });
  };

  const handleShowMapChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowMap(event.target.checked);
  };

  const getUniqueDepartmentIds = (
    organizationIds: string[],
    departmentIds: string[],
  ) => {
    const departmentSet = new Set(departmentIds);
    for (const organizationId of organizationIds) {
      const organization = sawmillUserInfo?.organizations.find(
        (organization) => organization.organizationId === organizationId,
      );
      if (organization) {
        for (const department of organization.departments) {
          departmentSet.add(department.departmentId);
        }
      }
    }
    return Array.from(departmentSet);
  };

  // parse filter options
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const from = query.get('from') ?? undefined;
    const to = query.get('to') ?? undefined;
    const page = query.get('page') ?? undefined;
    const pageSize = query.get('pageSize') ?? undefined;
    const organizationIds = query.getAll('organizationIds');
    const departmentIds = query.getAll('departmentIds');

    setSelectedOrganizationIds(organizationIds);
    setSelectedDepartmentIds(departmentIds);

    if (!from && !to) {
      // default date filter is last month
      query.set('from', lastMonth().toISOString());
      query.set('to', now().toISOString());
      history.replace({ search: query.toString() });
    } else if (sawmillUserInfo) {
      const newDepartmentOptions: { label: string; value: string }[] = [];
      setOrganizationOptions(
        sawmillUserInfo.organizations.map((organization) => {
          for (const department of organization.departments) {
            newDepartmentOptions.push({
              label: department.departmentName,
              value: department.departmentId,
            });
          }
          return {
            label: organization.organizationName,
            value: organization.organizationId,
          };
        }),
      );
      setDepartmentOptions(newDepartmentOptions);

      // set filters from query params
      setFilters({
        sawmillIds: sawmillUserInfo.sawmills.map(
          (sawmill) => sawmill.sawmillId,
        ),
        departmentIds: getUniqueDepartmentIds(organizationIds, departmentIds),
        from,
        to,
        page: page ? parseInt(page) : undefined,
        pageSize: pageSize ? parseInt(pageSize) : defaultPageSize,
      });
    }
  }, [location, sawmillUserInfo]);

  useEffect(() => {
    if (filters) {
      dispatch(stackActions.getStacksForSawmillUser(filters));
    }
  }, [filters]);

  useEffect(() => {
    dispatch(userActions.getSawmillUserInfo());
  }, []);

  return (
    <div className={classes.root}>
      <PageToolbar>
        <div className={classes.toolbar}>
          <Typography variant="subtitle1">{t('stacks_page.title')}</Typography>
          <div className={classes.flexGrow}></div>
          <FormControlLabel
            control={
              <Switch checked={showMap} onChange={handleShowMapChange} />
            }
            label={t('stacks_page.show_map')}
          />
        </div>
        <Divider />
        <div className={classes.filterContainer}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            className={classes.filterRow}
          >
            <DateRangePickerComponent
              itemClass={classes.filterSelect}
              clearable
              startDate={filters?.from ? new Date(filters.from) : null}
              endDate={filters?.to ? new Date(filters.to) : null}
              onChange={handleDateRangeChange}
            />
            <Grid item className={classes.filterSelect}>
              <MultiSelectFilter
                onSelect={(values) =>
                  updateListFilter('organizationIds', values)
                }
                values={selectedOrganizationIds}
                label="Organizations"
                items={organizationOptions}
              />
            </Grid>
            <Grid item className={classes.filterSelect}>
              <MultiSelectFilter
                onSelect={(values) => updateListFilter('departmentIds', values)}
                values={selectedDepartmentIds}
                label="Departments"
                items={departmentOptions}
              />
            </Grid>
          </Grid>
        </div>
        {t('stacks_page.showing_n_results', {
          count: stacks ? stacks.data.length : 0,
        })}
        {updatedAt && ` - ${updatedAt.toLocaleString()}`}
      </PageToolbar>
      <OverlaySpinner
        isLoading={isLoadingStacks === true || isLoadingCoordinates === true}
        className={classes.overlaySpinner}
      >
        {showMap && (
          <div className={classes.map}>
            <StacksMap disableStackDetailLinks />
          </div>
        )}
        <div className={classes.table}>
          <SawmillStacksTable
            onPageChanged={handlePageChanged}
            onPageSizeChanged={handlePageSizeChanged}
          />
        </div>
      </OverlaySpinner>
    </div>
  );
};

export { SawmillStacks };
